import { Radio } from 'antd';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo, { SpanStyle } from '@/components/Typography/Typo';
import { modalStyle } from '@/styles/style';
import { timeOut } from '@/utils/util';

type Props = {
  scale?: ChartScale;
  onDoneClick: (scale?: ChartScale) => void;
};

const ScaleOptionModal = (props: Props) => {
  const { closeModal } = useModalContext();
  const [scale, setScale] = useState<ChartScale | null>(props.scale || null);

  const validation = useMemo(() => {
    if (!scale)
      return {
        error: false,
      };
    if (scale.min > scale.max) {
      return {
        error: true,
        message: 'Min value should be less than max value',
      };
    }
    return {
      error: false,
    };
  }, [scale]);

  return (
    <Style g={32}>
      <VFlex g={24}>
        <Heading type="h3">Scale option</Heading>
        <Radio.Group
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            paddingLeft: 32,
          }}
          value={!!scale}
          onChange={(v) => setScale(v.target.value ? { min: 0, max: 0 } : null)}
        >
          <Radio value={false}>Auto scale</Radio>
          <VFlex g={16}>
            <Radio value={true}>Adjust scale option</Radio>
            <VFlex g={6} style={{ paddingLeft: 28 }}>
              <>
                <Flex className="input-section">
                  <Typo className="label">Min</Typo>
                  <Input
                    value={scale?.min || 0}
                    wrapperStyle={{ width: 100 }}
                    style={{ minWidth: 60 }}
                    disabled={!scale}
                    onChange={(e) =>
                      setScale((prev) => {
                        const numVal = Number(e.target.value);
                        if (Number.isNaN(numVal)) return prev;
                        const result = Math.max(numVal, 0);
                        if (!prev)
                          return {
                            min: result,
                            max: 0,
                          };
                        return { ...prev, min: result };
                      })
                    }
                  />
                </Flex>
                <Flex className="input-section">
                  <Typo className="label">Max</Typo>
                  <Input
                    value={scale?.max || 0}
                    wrapperStyle={{ width: 100 }}
                    style={{ minWidth: 60 }}
                    disabled={!scale}
                    onChange={(e) =>
                      setScale((prev) => {
                        const numVal = Number(e.target.value);
                        if (Number.isNaN(numVal)) return prev;
                        const result = Math.max(numVal, 0);
                        if (!prev)
                          return {
                            min: 0,
                            max: result,
                          };
                        return { ...prev, max: result };
                      })
                    }
                  />
                </Flex>
              </>

              {validation.error && (
                <Typo type="label3" className="text-destructive">
                  {validation.message}
                </Typo>
              )}
            </VFlex>
          </VFlex>
        </Radio.Group>
      </VFlex>

      <Flex j="flex-end" g={8}>
        <Button variant="outlined" sizeType="m" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          sizeType="m"
          disabled={validation.error}
          onClick={async () => {
            closeModal();
            await timeOut(200);
            props.onDoneClick(scale || undefined);
          }}
        >
          Done
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  width: 600px;
  padding: 32px;
  ${modalStyle};
  .input-section {
    gap: 6px;
    align-items: center;
    .label {
      width: 40px;
      ${SpanStyle({
        type: 'label3',
        weight: 'm',
      })}
    }
  }
`;

export default ScaleOptionModal;
